<script setup lang="ts">
import { onMounted, ref } from 'vue'
import { type IssueCase, issueCasesApi } from '@/api/entities/admin/issueCases'

definePage({
  meta: { title: 'Issue Cases', action: 'read', subject: 'review' },
})

const { snackbar, handleError } = useErrorHandler()
const loading = ref(false)
const items = ref<IssueCase[]>([])
const currentPage = ref(1)
const lastPage = ref(1)
const typeFilter = ref('')
const resolvedFilter = ref<boolean | null>(null)

const headers = [
  { title: 'ID', key: 'id', sortable: false },
  { title: 'Type', key: 'type', sortable: false },
  { title: 'Client', key: 'client_name', sortable: false },
  { title: 'Driver Config', key: 'driver_config_name', sortable: false },
  { title: 'Content', key: 'content', sortable: false },
  { title: '해결 여부', key: 'is_resolved', sortable: false },
  { title: 'Created At', key: 'created_at', sortable: false },
  { title: '작업', key: 'actions', sortable: false },
]

const loadItems = async () => {
  loading.value = true
  try {
    const { success, data } = await issueCasesApi.list({
      type: typeFilter.value || undefined,
      is_resolved: resolvedFilter.value ?? undefined,
      page: currentPage.value,
    })

    if (success && data) {
      items.value = data.data
      lastPage.value = data.last_page
    }
  }
  catch (error) {
    handleError(error, { showNotification: false })
  }
  finally {
    loading.value = false
  }
}

const toggleResolved = async (item: IssueCase) => {
  try {
    const { success, error } = await issueCasesApi.toggleResolved(item.id)
    if (success) {
      snackbar.success('상태가 변경되었습니다.')
      await loadItems()
    }
    else {
      snackbar.error(error?.message || '변경 실패')
    }
  }
  catch (error) {
    handleError(error)
  }
}

const onPageChange = (page: number) => {
  currentPage.value = page
  loadItems()
}

const formatDate = (d: string | null) => d ? new Date(d).toLocaleString('ko-KR') : '-'
const truncate = (s: string, n = 50) => s.length > n ? s.slice(0, n) + '...' : s

onMounted(loadItems)
</script>

<template>
  <VCard>
    <VCardTitle class="pa-4">
      Issue Cases
    </VCardTitle>
    <VCardText>
      <VRow class="mb-4">
        <VCol
          cols="12"
          md="3"
        >
          <VTextField
            v-model="typeFilter"
            label="Type 필터"
            clearable
            hide-details
          />
        </VCol>
        <VCol
          cols="12"
          md="3"
        >
          <VSelect
            v-model="resolvedFilter"
            :items="[{ title: '전체', value: null }, { title: '해결됨', value: true }, { title: '미해결', value: false }]"
            item-title="title"
            item-value="value"
            label="해결 여부"
            hide-details
          />
        </VCol>
        <VCol
          cols="12"
          md="2"
        >
          <VBtn
            color="primary"
            @click="loadItems"
          >
            검색
          </VBtn>
        </VCol>
      </VRow>
      <VDataTable
        :headers="headers"
        :items="items"
        :loading="loading"
        hide-default-footer
      >
        <template #item.content="{ item }">
          {{ truncate(item.content) }}
        </template>
        <template #item.is_resolved="{ item }">
          <VChip
            :color="item.is_resolved ? 'success' : 'warning'"
            size="small"
          >
            {{ item.is_resolved ? '해결됨' : '미해결' }}
          </VChip>
        </template>
        <template #item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>
        <template #item.actions="{ item }">
          <VBtn
            size="small"
            :color="item.is_resolved ? 'warning' : 'success'"
            @click="toggleResolved(item)"
          >
            {{ item.is_resolved ? '미해결로' : '해결 완료' }}
          </VBtn>
        </template>
        <template #no-data>
          <div class="text-center py-8">
            데이터가 없습니다.
          </div>
        </template>
      </VDataTable>
      <div
        v-if="lastPage > 1"
        class="d-flex justify-center mt-4"
      >
        <VPagination
          :model-value="currentPage"
          :length="lastPage"
          @update:model-value="onPageChange"
        />
      </div>
    </VCardText>
  </VCard>
  <VSnackbar
    v-model="snackbar.isVisible.value"
    :color="snackbar.color.value"
    :timeout="snackbar.timeout.value"
  >
    {{ snackbar.message.value }}
  </VSnackbar>
</template>
